import {useMemo, useState} from 'react'
import {ActivityIndicator, StyleSheet, View} from 'react-native'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'

// import {DM_SERVICE_HEADERS} from '#/lib/constants'
import {cleanError} from '#/lib/strings/errors'
import {useAgent, useSession, useSessionApi} from '#/state/session'
import {agentToSessionAccount} from '#/state/session/agent'
import {useLoggedOutViewControls} from '#/state/shell/logged-out'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button.tsx'
import {FormError} from '#/components/forms/FormError'
import {CircleInfo_Stroke2_Corner0_Rounded as CircleInfo} from '#/components/icons/CircleInfo'
import {Text} from '#/components/Typography'
import {FormContainer} from './FormContainer'

export function EmailFlow({
  error,
  onPressBack,
  emailAlreadySent = false,
}: {
  error: string
  onPressBack: () => void
  /** True when requestDeleteAccount already triggered the email send */
  emailAlreadySent?: boolean
}) {
  const t = useTheme()
  const {_} = useLingui()
  const agent = useAgent()
  const {currentAccount} = useSession()
  const {logoutEveryAccount, removeAccount} = useSessionApi()
  const {setShowLoggedOut} = useLoggedOutViewControls()
  const account = useMemo(() => agentToSessionAccount(agent), [agent])

  const [isEmailSent, setIsEmailSent] = useState(emailAlreadySent)
  const [confirmCode, setConfirmCode] = useState('')
  const [password, setPassword] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [localError, setLocalError] = useState('')

  const onPressSendEmail = async () => {
    setLocalError('')
    setIsProcessing(true)
    try {
      await agent.com.atproto.server.requestAccountDelete()
      setIsEmailSent(true)
    } catch (e: unknown) {
      setLocalError(cleanError(e))
    }
    setIsProcessing(false)
  }

  const onPressConfirmDelete = async () => {
    if (!currentAccount?.did) return
    setLocalError('')
    setIsProcessing(true)
    try {
      // await agent.api.chat.bsky.actor.deleteAccount(undefined, {
      //   headers: DM_SERVICE_HEADERS,
      // })
      await agent.com.atproto.server.deleteAccount({
        did: currentAccount.did,
        password,
        token: confirmCode.replace(/\s/g, ''),
      })
      if (account) removeAccount(account)
      logoutEveryAccount('Settings')
      setShowLoggedOut(true)
    } catch (e: unknown) {
      setLocalError(cleanError(e))
    }
    setIsProcessing(false)
  }

  return (
    <FormContainer testID="loginForm" style={{backgroundColor: 'transparent'}}>
      <View style={[a.flex_1, a.justify_center, a.align_center]}>
        <Text
          style={[
            a.text_sm,
            a.font_medium,
            t.atoms.text_contrast_medium,
            a.mb_sm,
            styles.labelText,
          ]}>
          {/*<Trans>Remove user</Trans>*/}
        </Text>

        {!isEmailSent ? (
          // Step 1: matches original Bluesky DeleteAccount dialog
          <View style={[a.gap_md, a.mt_md, {maxWidth: 300}]}>
            <Text style={[styles.description, t.atoms.text]}>
              <Trans>
                For security reasons, we'll need to send a confirmation code to
                your email address.
              </Trans>
            </Text>

            {localError ? (
              <Text
                style={[
                  a.text_sm,
                  {color: t.palette.negative_500, textAlign: 'center'},
                ]}>
                {localError}
              </Text>
            ) : null}

            {isProcessing ? (
              <View style={styles.btn}>
                <ActivityIndicator color="#fff" />
              </View>
            ) : (
              <>
                <Button
                  label={_(msg`Send Email`)}
                  variant="solid"
                  color="primary"
                  size="large"
                  onPress={() => {
                    void onPressSendEmail()
                  }}>
                  <ButtonText>
                    <Trans>Send Email</Trans>
                  </ButtonText>
                </Button>
                <Button
                  label={_(msg`Cancel`)}
                  variant="solid"
                  color="secondary"
                  size="large"
                  onPress={onPressBack}>
                  <ButtonText>
                    <Trans>Cancel</Trans>
                  </ButtonText>
                </Button>
              </>
            )}

            <View
              style={[
                a.w_full,
                a.flex_row,
                a.gap_sm,
                a.mt_sm,
                a.p_lg,
                a.rounded_sm,
                t.atoms.bg_contrast_25,
              ]}>
              <CircleInfo size="md" style={[a.relative, {top: -1}]} />
              <Text style={[a.leading_snug, a.flex_1, a.text_sm]}>
                <Trans>
                  You can also temporarily deactivate your account instead, and
                  reactivate it at any time.
                </Trans>
              </Text>
            </View>
          </View>
        ) : (
          // Step 2: enter confirmation code and password
          <View
            style={[
              a.align_center,
              a.mt_md,
              a.flex_1,
              a.gap_md,
              {width: '100%', maxWidth: 300},
            ]}>
            <Text
              style={[
                a.text_sm,
                t.atoms.text_contrast_medium,
                {textAlign: 'center'},
              ]}>
              <Trans>
                Check your inbox for a confirmation code and enter it below:
              </Trans>
            </Text>
            <View style={{width: '100%'}}>
              <Text
                style={[
                  a.text_xs,
                  t.atoms.text_contrast_medium,
                  styles.fieldLabel,
                ]}>
                <Trans>Confirmation code</Trans>
              </Text>
              <input
                type="text"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                name="delete-confirm-token"
                value={confirmCode}
                onChange={e => setConfirmCode(e.target.value)}
                style={inputStyle}
              />
            </View>
            <View style={{width: '100%'}}>
              <Text
                style={[
                  a.text_xs,
                  t.atoms.text_contrast_medium,
                  styles.fieldLabel,
                ]}>
                <Trans>Password</Trans>
              </Text>
              <input
                type="password"
                autoComplete="new-password"
                name="delete-confirm-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                style={inputStyle}
              />
            </View>

            {localError ? (
              <Text
                style={[
                  a.text_sm,
                  {color: t.palette.negative_500, textAlign: 'center'},
                ]}>
                {localError}
              </Text>
            ) : null}

            <Button
              label={_(msg`Delete account`)}
              variant="solid"
              color="negative"
              size="large"
              disabled={isProcessing || !confirmCode || !password}
              onPress={() => {
                void onPressConfirmDelete()
              }}>
              <ButtonText>
                {isProcessing ? (
                  <Trans>Deleting...</Trans>
                ) : (
                  <Trans>Delete account</Trans>
                )}
              </ButtonText>
            </Button>
          </View>
        )}
      </View>

      <FormError error={error} />

      {isEmailSent && (
        <View style={[a.flex_row, a.align_center, a.pt_md, a.self_center]}>
          <button className={'BackButton'} onClick={onPressBack}>
            <Trans>Back</Trans>
          </button>
          <View style={a.flex_1} />
        </View>
      )}
    </FormContainer>
  )
}

const inputStyle: React.CSSProperties = {
  backgroundColor: 'rgba(60, 48, 76, 0.3)',
  borderRadius: '10px',
  fontSize: '16px',
  padding: '10px 20px',
  border: '1px solid #3c304c',
  color: '#a6a0ac',
  width: '100%',
  boxSizing: 'border-box',
  outline: 'none',
}

const styles = StyleSheet.create({
  labelText: {
    textAlign: 'center',
    fontSize: 16,
  },
  description: {
    textAlign: 'center',
    paddingHorizontal: 22,
    marginBottom: 10,
    fontSize: 16,
  },
  btn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 32,
    padding: 14,
  },
  cancelButton: {
    marginTop: 4,
  },
  fieldLabel: {
    marginBottom: 4,
    marginLeft: 4,
  },
})
